import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, Heading, Text } from '@restworld/ui-ds-base';
import { Card, CompanyReviewProps, PrimaryCardHeader } from '../../card';

type ApplyConfirmationProps = {
  onProceedWithApplication: () => void;
  onChangeMind: () => void;
  mainTitle?: string;
  proceedButtonTitle?: string;
  exitButtonTitle?: string;
  children?: React.ReactNode;
  restaurantID?: string;
  loading?: boolean;
  /** Shown above the buttons when the last attempt failed; the buttons stay active. */
  errorMessage?: string;
  restaurantPopupInfo?: {
    restaurantEmployerName?: string;
    restaurantName?: string;
    restaurantAddress?: string;
  };
};

export const ApplyConfirmation = ({
  onProceedWithApplication,
  onChangeMind,
  mainTitle,
  proceedButtonTitle,
  exitButtonTitle,
  children: cardDetailAsChildren,
  loading,
  restaurantPopupInfo,
  errorMessage,
}: ApplyConfirmationProps) => {
  const { t } = useTranslation('onboarding-page');
  const companyReviewDetails = useMemo(
    () =>
      restaurantPopupInfo
        ? ({
            showRestaurant: true,
            companyLogo: '',
            seasonalAccommodation: false,
            companyName: restaurantPopupInfo?.restaurantEmployerName ?? '',
            title: restaurantPopupInfo?.restaurantName ?? '',
            jobLocation: restaurantPopupInfo?.restaurantAddress ?? '',
            jobLocality: '',
            fontColor: 'white',
          } as CompanyReviewProps)
        : null,
    [restaurantPopupInfo]
  );
  return (
    <div className="flex flex-col gap-4 w-full">
      <Heading size={3} weight="semibold" align="center" className="leading-7">
        {mainTitle ?? t('youre_about_to_apply_spontaneously')}
      </Heading>
      {!mainTitle && (
        <Heading size={5} weight="medium" color="neutral" align="center" className="max-w-[450px] mx-auto">
          {t('if_interested_the_company_will_contact_you_directly_key')}
        </Heading>
      )}
      {cardDetailAsChildren ? (
        <div className="mx-auto mt-8">{cardDetailAsChildren}</div>
      ) : companyReviewDetails ? (
        <Card className={'max-w-xs'} variant="gradientBase">
          <PrimaryCardHeader
            {...{
              ...companyReviewDetails,
              className: 'mt-0',
            }}
          />
        </Card>
      ) : null}
      {errorMessage ? (
        <div role="alert">
          <Text as="p" size={3} color="error" align="center">
            {errorMessage}
          </Text>
        </div>
      ) : null}
      <div className="flex flex-col gap-2 pb-4">
        <Button variant="primary" fullwidth onClick={onProceedWithApplication} loading={loading}>
          {proceedButtonTitle ?? t('proceed_with_the_application')}
        </Button>
        <Button variant="secondary-neutral" fullwidth onClick={onChangeMind}>
          {exitButtonTitle ?? t('changed_my_mind')}
        </Button>
      </div>
    </div>
  );
};
